/**
 * CRM Authentication Context
 * Provides CRM user state and auth methods throughout the app
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getCrmToken, setCrmToken, clearCrmToken, crmFetch } from '../utils/crmAuth';
import { isCrmEnabled } from '../utils/featureFlags';
import { API_BASE } from '../config/api';

const CrmAuthContext = createContext(null);

export const useCrmAuth = () => {
  const context = useContext(CrmAuthContext);
  if (!context) {
    throw new Error('useCrmAuth must be used within CrmAuthProvider');
  }
  return context;
};

export const CrmAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Bootstrap: Check if user is authenticated on mount
  useEffect(() => {
    if (!isCrmEnabled()) {
      setLoading(false);
      return;
    }

    const bootstrapAuth = async () => {
      const token = getCrmToken();
      
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        // Verify token by calling /me endpoint
        const response = await crmFetch('/api/crm/auth/me');
        
        if (response.ok) {
          const data = await response.json();
          setUser(data);
          
          // Fetch profile if user is authenticated
          await fetchProfile();
        } else {
          // Token invalid - clear it
          clearCrmToken();
          setUser(null);
          setProfile(null);
        }
      } catch (error) {
        console.error('Error bootstrapping CRM auth:', error);
        clearCrmToken();
        setUser(null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();
  }, []);

  const fetchProfile = async () => {
    if (!isCrmEnabled() || !user) {
      return;
    }

    try {
      setProfileLoading(true);
      const response = await crmFetch('/api/crm/profiles/me');
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      } else if (response.status === 404) {
        // Profile not found - this is expected for new users
        setProfile(null);
      } else {
        // Other error - clear profile
        setProfile(null);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
      setProfile(null);
    } finally {
      setProfileLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE}/api/crm/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login failed');
      }

      // Store token and user
      setCrmToken(data.access_token);
      setUser(data.user);
      
      // Fetch profile after login
      await fetchProfile();

      return { success: true, user: data.user };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint (optional - stateless JWT)
      await crmFetch('/api/crm/auth/logout');
    } catch (error) {
      // Ignore errors - we'll clear token anyway
    } finally {
      clearCrmToken();
      setUser(null);
      navigate('/crm/counselor/login');
    }
  };

  const value = {
    user,
    profile,
    profileLoading,
    loading,
    login,
    logout,
    fetchProfile,
    isAuthenticated: !!user,
    hasProfile: !!profile,
  };

  return <CrmAuthContext.Provider value={value}>{children}</CrmAuthContext.Provider>;
};
